/**
 * Wheel of Wonder screen (NAMED-ONLY — docs/BARRAGE_IMPLEMENTATION_PLAN.md §2;
 * legacy AVOID_LEGACY_ANALYSIS.md §2.7 "Wheels & events"). Two prize wheels:
 *  - the **daily free wheel** ("Wonder", 8 slots) with a 24h cooldown, and
 *  - a **token-trade wheel** (12 slots) you pay tokens to spin.
 *
 * CONTRACT NOTE: the authoritative slot table + odds + cost + cooldown live in
 * Firestore `config/wheel` and are applied SERVER-SIDE (the client never decides
 * the outcome — a hard improvement over the legacy client-authoritative spin,
 * §"Anti-cheat"). The {@link GameServices} facade currently exposes only {@link
 * GameServices.spinWheel}, which returns a server-resolved {@link WheelResult}; it
 * does NOT yet expose the wheel config. So this screen renders a fixed, evenly
 * weighted wheel face for layout/animation and drives the *reveal* purely from
 * the server's `slotIndex` / prize fields. When a `getWheelConfig` read lands on
 * the facade, swap {@link WHEELS} for the live config — nothing else changes.
 *
 * Presentational + thin orchestration only: screen-local UI state, all writes via
 * the facade, authoritative token balance read back from the {@link WheelResult}.
 * The 24h cooldown shown here is a courtesy gate; the SERVER is the real gate and
 * a premature spin simply throws (surfaced inline). Anonymous callers see the
 * "log in to collect" tease via {@link RequiresAccountError}.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from './i18n';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { WheelResult } from '../services/dataModel';

interface WheelScreenProps {
  /** Tier-aware data facade. {@link GameServices.spinWheel} is NAMED-ONLY. */
  services: GameServices;
  /** Initial token balance to display (re-read from each spin's result). */
  tokens?: number;
  /** Return to the previous screen (parent owns navigation). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own header. */
  embedded?: boolean;
  /** Notify the parent that the token/XP balance changed (a spin), to refresh chrome. */
  onEconomyChange?: () => void;
}

/** Static descriptor of a wheel face (visual only; the server owns real odds). */
interface WheelDef {
  /** Server-side wheel id passed to {@link GameServices.spinWheel}. */
  wheelId: string;
  label: string;
  /** Short pitch shown under the title. */
  blurb: string;
  /** Number of evenly-spaced slots drawn on the face (legacy: 8 / 12). */
  slots: number;
  /** Token cost to spin (0 = the free daily wheel). Display only. */
  cost: number;
  /** Cooldown in ms before the next free spin (display courtesy gate). */
  cooldownMs: number;
  /**
   * Display-only slot labels drawn around the face, in position order. Purely
   * cosmetic — the SERVER decides the real prize. Length need not equal `slots`;
   * it's cycled to fill the wheel so the face always reads as a prize wheel.
   */
  faceLabels: readonly string[];
}

/** 24 hours in ms — the daily wheel's cooldown (legacy `TIMER_WHEELOFWONDER`). */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The two wheels (visual definitions). Slot counts + cooldown mirror the legacy
 * spec; the real cost/odds are enforced server-side. The first wheel is the daily
 * free spin, the second the paid token-trade wheel.
 */
const WHEELS: readonly WheelDef[] = [
  {
    wheelId: 'wonder',
    label: 'Wheel of Wonder',
    blurb: 'One free spin a day — tokens, XP and the odd secret sticker.',
    slots: 8,
    cost: 0,
    cooldownMs: DAY_MS,
    faceLabels: ['100 XP', '3◉', '25 XP', '5◉', 'Sticker', '10◉', '50 XP', '5◉'],
  },
  {
    wheelId: 'token-trade',
    label: 'Token Trade',
    blurb: 'Spend tokens for a shot at bigger prizes.',
    slots: 12,
    cost: 5,
    cooldownMs: 0,
    faceLabels: [
      '5◉',
      '10 XP',
      '10◉',
      'Sticker',
      '15◉',
      '25 XP',
      '10◉',
      '50 XP',
      '15◉',
      'Sticker',
      '20◉',
      '100 XP',
    ],
  },
];

/** localStorage key holding the epoch-ms of the last free daily spin. */
const FREE_SPIN_KEY = 'barrage.wheel.lastFreeSpin';

/** How long the disc tween runs, in ms — kept in sync with the inline CSS below. */
const SPIN_MS = 4200;
/** Full rotations the disc makes before settling on the winning slot. */
const SPIN_TURNS = 5;

/** Read the last-free-spin timestamp (0 if none / unavailable). */
function loadLastFreeSpin(): number {
  try {
    return Number(window.localStorage.getItem(FREE_SPIN_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Persist the last-free-spin timestamp (best-effort). */
function saveLastFreeSpin(t: number): void {
  try {
    window.localStorage.setItem(FREE_SPIN_KEY, String(t));
  } catch {
    /* private mode / quota — the server cooldown still protects us. */
  }
}

/** Human "Xh Ym" remaining string for the cooldown countdown. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** A one-line summary of what a spin awarded, for the reveal banner. */
function describePrize(r: WheelResult): string {
  switch (r.prizeType) {
    case 'tokens':
      return `+${r.qty.toLocaleString()} tokens`;
    case 'xp':
      return `+${r.qty.toLocaleString()} XP`;
    case 'sticker':
      return 'A new sticker!';
    default:
      return 'A prize!';
  }
}

/** A celebratory toast line for the reveal moment. */
function celebrate(r: WheelResult): string {
  return r.prizeType === 'sticker' ? 'A rare sticker is yours!' : `You won ${describePrize(r)}!`;
}

export function WheelScreen({
  services,
  tokens = 0,
  onBack,
  embedded = false,
  onEconomyChange,
}: WheelScreenProps) {
  const t = useT();
  const [wheelIdx, setWheelIdx] = useState(0);
  const wheel = WHEELS[wheelIdx];

  // Authoritative token balance: seeded from props, replaced by each spin result.
  const [balance, setBalance] = useState(tokens);
  useEffect(() => setBalance(tokens), [tokens]);

  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0); // accumulated degrees on the face.
  const [result, setResult] = useState<WheelResult | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsAccount, setNeedsAccount] = useState(false);

  // Daily free-spin cooldown (courtesy gate; the server is authoritative).
  const [lastFreeSpin, setLastFreeSpin] = useState<number>(() => loadLastFreeSpin());
  const [now, setNow] = useState<number>(() => Date.now());

  // Remaining cooldown for the free wheel (0 for the paid wheel / when ready).
  const freeRemaining = useMemo(() => {
    if (wheel.cost > 0) return 0;
    return Math.max(0, lastFreeSpin + wheel.cooldownMs - now);
  }, [wheel.cost, wheel.cooldownMs, lastFreeSpin, now]);

  // Tick a 1s clock only while a free-wheel cooldown is actually pending.
  useEffect(() => {
    if (freeRemaining <= 0) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [freeRemaining]);

  // Clear any pending reveal / toast timers on unmount.
  const revealTimer = useRef<number | null>(null);
  const toastTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (revealTimer.current !== null) window.clearTimeout(revealTimer.current);
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const onPickWheel = useCallback((idx: number) => {
    setWheelIdx(idx);
    setResult(null);
    setToast(null);
    setError(null);
  }, []);

  const canSpin =
    !spinning &&
    !needsAccount &&
    (wheel.cost === 0 ? freeRemaining <= 0 : balance >= wheel.cost);

  /**
   * Spin: ask the server for the outcome, then animate the face so the winning
   * slot lands centred under the top pointer, then reveal the prize. The grant is
   * already applied server-side — we only read `tokensAfter` back.
   */
  const onSpin = useCallback(async () => {
    if (!canSpin) return;
    setSpinning(true);
    setError(null);
    setResult(null);
    setToast(null);
    if (toastTimer.current !== null) {
      window.clearTimeout(toastTimer.current);
      toastTimer.current = null;
    }
    try {
      const res = await services.spinWheel(wheel.wheelId);

      // Land the WINNING slot centred under the top pointer. The conic-gradient
      // face starts at 12 o'clock and runs clockwise, so slot `idx` (0-based)
      // spans [idx*perSlot, (idx+1)*perSlot)°; its centre is (idx+0.5)*perSlot.
      // A clockwise disc rotation R brings a centre at angle c to the top when
      // c + R ≡ 0 (mod 360), i.e. R = 360 − c (plus whole turns of flair).
      const slotCount = Math.max(1, wheel.slots);
      const idx = (((res.slotIndex - 1) % slotCount) + slotCount) % slotCount;
      const perSlot = 360 / slotCount;
      const centre = (idx + 0.5) * perSlot;
      const base = Math.ceil(rotation / 360) * 360; // snap to a whole turn first.
      const target = base + SPIN_TURNS * 360 + ((360 - centre) % 360);
      setRotation(target);

      // Update the authoritative balance immediately; reveal after the tween. Tell
      // the shell the balance moved so the chrome profile card re-reads it.
      setBalance(res.tokensAfter);
      onEconomyChange?.();
      if (wheel.cost === 0) {
        const t = Date.now();
        setLastFreeSpin(t);
        saveLastFreeSpin(t);
        setNow(t);
      }

      revealTimer.current = window.setTimeout(() => {
        setResult(res);
        setSpinning(false);
        revealTimer.current = null;
        // Celebratory toast that auto-dismisses; the reveal banner persists.
        setToast(celebrate(res));
        toastTimer.current = window.setTimeout(() => {
          setToast(null);
          toastTimer.current = null;
        }, 2800);
      }, SPIN_MS);
    } catch (err) {
      setSpinning(false);
      if (err instanceof RequiresAccountError) setNeedsAccount(true);
      else setError(err instanceof Error ? err.message : 'The spin failed — try again.');
    }
  }, [canSpin, services, wheel.wheelId, wheel.slots, wheel.cost, rotation, onEconomyChange]);

  // Pre-compute the conic-gradient wedge backdrop for the active wheel face. The
  // palette alternates warm/cool wedges so neighbouring slots stay distinct.
  const wheelFace = useMemo(() => {
    const n = Math.max(1, wheel.slots);
    const step = 360 / n;
    const palette = [
      'var(--accent)',
      'var(--gold)',
      'var(--accent-2)',
      'var(--accent-soft)',
      'var(--gold-deep)',
      '#7a4cff',
    ];
    const stops: string[] = [];
    for (let i = 0; i < n; i++) {
      const c = palette[i % palette.length];
      stops.push(`${c} ${i * step}deg ${(i + 1) * step}deg`);
    }
    return `conic-gradient(from 0deg, ${stops.join(',')})`;
  }, [wheel.slots]);

  // Display-only labels positioned radially around the face, one per slot. Each
  // sits at its slot centre and counter-rotates so the text reads upright.
  const faceSlots = useMemo(() => {
    const n = Math.max(1, wheel.slots);
    const perSlot = 360 / n;
    const labels = wheel.faceLabels;
    return Array.from({ length: n }, (_, i) => ({
      key: i,
      angle: (i + 0.5) * perSlot,
      text: labels.length > 0 ? labels[i % labels.length] : '',
    }));
  }, [wheel.slots, wheel.faceLabels]);

  return (
    <div className={'screen wheel-screen' + (embedded ? ' is-embedded' : '')}>
      {!embedded && (
        <header className="wheel-head">
          <button className="btn btn-ghost shop-back" onClick={onBack}>
            ‹ Back
          </button>
          <h1 className="wheel-title">{t('wheel.title')}</h1>
          <div className="shop-wallet" title="Token balance">
            <span className="token-coin" aria-hidden="true">
              ◉
            </span>
            <span className="token-amount">{balance.toLocaleString()}</span>
          </div>
        </header>
      )}

      {needsAccount ? (
        <section className="panel tease">
          <h2>Log in to collect</h2>
          <p className="tease-copy">
            The Wheel of Wonder is a named-account perk. Anonymous play keeps the game
            and the leaderboard — sign up to start spinning for stickers and tokens.
          </p>
        </section>
      ) : (
        <>
          <div className="wheel-tabs" role="tablist" aria-label="Choose a wheel">
            {WHEELS.map((w, i) => (
              <button
                key={w.wheelId}
                role="tab"
                aria-selected={wheelIdx === i}
                className={wheelIdx === i ? 'shop-tab active' : 'shop-tab'}
                onClick={() => onPickWheel(i)}
                disabled={spinning}
              >
                {w.label}
                {w.cost > 0 && <span className="wheel-tab-cost"> · {w.cost}◉</span>}
              </button>
            ))}
          </div>

          <p className="wheel-note" style={{ textAlign: 'center', maxWidth: 'min(360px, 90vw)' }}>
            {wheel.blurb}
          </p>

          {/* The wheel face. The pointer sits at the top; the disc rotates under it. */}
          <div className="wheel-stage">
            <span className="wheel-pointer" aria-hidden="true" />
            <div
              className="wheel-disc"
              role="img"
              aria-label={`${wheel.label}: ${wheel.slots} prize slots`}
              style={{
                background: wheelFace,
                transform: `rotate(${rotation}deg)`,
                transition: spinning
                  ? `transform ${SPIN_MS / 1000}s cubic-bezier(0.16, 1, 0.3, 1)`
                  : 'none',
              }}
            >
              {/* Radial prize labels (display only — server owns real odds). The
                  geometry (position/rotation) is intrinsic to the wheel and lives
                  inline; colours come from the design-system tokens. Each label is
                  a thin full-height spoke rotated to its slot centre; the text is
                  pushed toward the rim and counter-rotated so it reads upright. */}
              {faceSlots.map((s) => (
                <span
                  key={s.key}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '50%',
                    width: 0,
                    transform: `translateX(-50%) rotate(${s.angle}deg)`,
                    pointerEvents: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '11%',
                      left: '50%',
                      transform: `translateX(-50%) rotate(${-s.angle}deg)`,
                      whiteSpace: 'nowrap',
                      fontSize: 'clamp(10px, 3vw, 13px)',
                      fontWeight: 800,
                      letterSpacing: '0.02em',
                      color: 'var(--accent-ink)',
                      textShadow: '0 1px 2px rgba(255, 255, 255, 0.35)',
                    }}
                  >
                    {s.text}
                  </span>
                </span>
              ))}
              <span className="wheel-hub" aria-hidden="true">
                ★
              </span>
            </div>
          </div>

          {/* Reveal banner — celebratory once the disc lands. */}
          {result && !spinning && (
            <div className="wheel-reveal" role="status">
              <p className="wheel-reveal-kicker">{t('wheel.youWon')}</p>
              <p className="wheel-reveal-prize">{describePrize(result)}</p>
              <p className="wheel-reveal-balance">
                {t('wheel.balance')}: {result.tokensAfter.toLocaleString()} ◉
              </p>
            </div>
          )}

          {error && <p className="shop-error">{error}</p>}

          {/* Spin control + cost / cooldown messaging. */}
          <div className="wheel-controls">
            <button
              className="btn btn-primary wheel-spin"
              disabled={!canSpin}
              onClick={() => void onSpin()}
            >
              {spinning
                ? t('wheel.spinning')
                : wheel.cost === 0
                  ? freeRemaining > 0
                    ? `Free spin in ${formatRemaining(freeRemaining)}`
                    : t('wheel.freeSpin')
                  : `Spin · ${wheel.cost}◉`}
            </button>

            {wheel.cost === 0 ? (
              freeRemaining > 0 ? (
                <p className="wheel-note">
                  Come back in {formatRemaining(freeRemaining)} for your next free spin.
                </p>
              ) : (
                !spinning && <p className="wheel-note">Your daily free spin is ready.</p>
              )
            ) : balance < wheel.cost && !spinning ? (
              <p className="wheel-note">Not enough tokens — earn more on the free wheel.</p>
            ) : (
              !spinning && (
                <p className="wheel-note">Costs {wheel.cost}◉ per spin.</p>
              )
            )}
          </div>
        </>
      )}

      {toast && (
        <div className="wheel-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
